import React from 'react';

/**
 * function to retry a promise
 * @param fn the promise function
 * @param numRetries the number of times to retry the promise
 * @param interval in milliseconds between each retry
 */
export const retryPromise = <T,>(
  fn: () => Promise<T>,
  numRetries = 5,
  interval = 1000
): Promise<T> => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: Error) => {
        if (numRetries <= 0) {
          reject(error);
          return;
        }

        setTimeout(() => {
          retryPromise(fn, numRetries - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

/**
 * Wrapper around React lazy that includes retrying
 * retry is necessary for mitigating ChunkLoadError when a new version is deployed
 * @param importFunction the same import function that would be passed to React.lazy
 * @param fallback the loading indicator shown while the import is loading
 */
export const safeLazy = <T,>(
  importFunction: () => Promise<{ default: React.ComponentType<T> }>,
  fallback: React.ReactNode
) => {
  if (process.env.NODE_ENV === 'development') {
    // Eager + Suspense-free in dev so Fast Refresh works
    let Component: React.ComponentType<T> | null = null;
    void importFunction().then((m) => {
      Component = m.default;
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore props type
    return (props) => (Component ? <Component {...props} /> : fallback);
  }
  // todo update fallback with a default of <LoadingSpinner /> after refactoring loading spinner into packages to avoid circular dependencies
  const LazyComponent = React.lazy(() => retryPromise(importFunction));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore props type
  return (props) => {
    return (
      <React.Suspense fallback={fallback}>
        <LazyComponent {...props} />
      </React.Suspense>
    );
  };
};
